import React, { useMemo, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  NMForm,
  Col,
  Button,
  Row,
  DataGridView,
} from "@neumetric/blueprint-lib/components";
import {
  eduActions,
  getOrganisationsForScheduler,
  getUsersForScheduler,
  getActionsForScheduler,
  getEducatorScheduleRows,
} from "../../../store";

const PAGE_SIZE = 9;

const SchedulerFields = [
  {
    label: "Organisation",
    attribute: "organisations",
    typeRepresentation: "DROPDOWN",
    placeholder: "select organisation",
    classes: { root: "w-19", label: "w-100-px" },
  },
  {
    label: "User",
    attribute: "user",
    typeRepresentation: "DROPDOWN",
    placeholder: "select user",
    classes: { root: "w-19", label: "w-100-px" },
  },
  {
    label: "Start date",
    attribute: "start_date",
    typeRepresentation: "DATE",
    placeholder: "select date",
    classes: { root: "w-19", label: "w-100-px" },
  },
  {
    label: "End date",
    attribute: "end_date",
    typeRepresentation: "DATE",
    placeholder: "select date",
    classes: { root: "w-19", label: "w-100-px" },
  },
  {
    label: "Action",
    attribute: "action",
    typeRepresentation: "DROPDOWN",
    placeholder: "select action",
    classes: { root: "w-19", label: "w-100-px" },
  },
];

const Columns = [
  {
    dataIndex: "title",
    typeRepresentation: "TEXT",
    title: "Title",
    width: "33%",
  },
  {
    dataIndex: "trigger_at",
    typeRepresentation: "TEXT",
    title: "Trigger At",
    width: "18%",
  },
  {
    dataIndex: "name",
    typeRepresentation: "TEXT",
    title: "Name",
    width: "15%",
  },
  {
    dataIndex: "email",
    typeRepresentation: "TEXT",
    title: "Email",
    width: "20%",
  },
  {
    dataIndex: "status",
    typeRepresentation: "TEXT",
    title: "Status",
    width: "13%",
  },
];

const toYMD = (val) => {
  if (!val) return null;
  if (typeof val === "string") return val.slice(0, 10);
  if (val instanceof Date) {
    const yyyy = val.getFullYear();
    const mm = String(val.getMonth() + 1).padStart(2, "0");
    const dd = String(val.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  if (val?.format) return val.format("YYYY-MM-DD");
  return null;
};

export const EducatorScheduleScreen = ({ triggerType }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const organisationsList = useSelector(getOrganisationsForScheduler) || [];
  const usersList = useSelector(getUsersForScheduler) || [];
  const actionsList = useSelector(getActionsForScheduler) || [];
  const tableRows = useSelector(getEducatorScheduleRows) || [];
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    dispatch(eduActions.fetchOrganisationsForScheduler());
    dispatch(eduActions.fetchActionsForScheduler());
  }, [dispatch]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return tableRows.slice(start, start + PAGE_SIZE);
  }, [tableRows, page]);

  const resetScreenState = () => {
    setSelectedKeys([]);
    setPage(1);
    formRef.current?.reset?.();
  };

  const getOptions = (attribute) => {
    if (attribute === "organisations") {
      return organisationsList.map((o) => ({
        id: String(o.id),
        label: o.name,
      }));
    }
    if (attribute === "user") {
      return (usersList || []).map((u) => ({
        id: String(u.id),
        label: u.name || u.email,
      }));
    }
    if (attribute === "action") {
      return (actionsList || []).map((a) => ({
        id: a.key,
        label: a.label,
      }));
    }
    return [];
  };

  const handleFormChange = (attribute, value) => {
    setFormValues((prev) => ({
      ...prev,
      [attribute]: value,
    }));

    if (attribute === "organisations") {
      formRef.current?.setValue?.("user", null);

      setFormValues((prev) => ({
        ...prev,
        user: null,
      }));

      if (value) {
        dispatch(eduActions.fetchUsersForScheduler({ org_id: value }));
      }
    }
  };

  const buildPayloadFromForm = () => {
    const values = formRef.current?.getValues?.() || {};
    return {
      org_id: values.organisations ? Number(values.organisations) : null,
      user_id: values.user ? Number(values.user) : null,
      trigger_type: triggerType,
      action_filter: values.action || null,
      start_date: toYMD(values.start_date),
      end_date: toYMD(values.end_date),
      page: 1,
    };
  };

  const canSearch = Boolean(formValues?.organisations);
  const canExecute = selectedKeys.length > 0;

  const handleSearch = () => {
    dispatch(eduActions.fetchListOfEducatorItems(buildPayloadFromForm()));
  };

  const handleExecute = () => {
    const et_ids = selectedKeys.map(Number).filter(Boolean);
    if (!et_ids.length) return;

    dispatch(
      eduActions.triggerEducatorItems({
        et_ids,
        trigger_type: triggerType,
      }),
    );

    resetScreenState();
  };
  const computedFields = useMemo(() => {
    return SchedulerFields.map((field) => {
      if (field.attribute === "user") {
        return {
          ...field,
          disabled: !formValues.organisations,
        };
      }

      if (field.attribute === "action") {
        return {
          ...field,
          disabled: !formValues.start_date || !formValues.end_date,
        };
      }

      return field;
    });
  }, [formValues]);

  const handleClear = () => {
    const emptyValues = {
      organisations: null,
      user: null,
      start_date: null,
      end_date: null,
      action: null,
    };

    setFormValues(emptyValues);
    formRef.current?.updateFormData(emptyValues);

    setSelectedKeys([]);
    setPage(1);
  };

  return (
    <>
      <Row className="w-100 mt-20">
        <Col className="w-70 pl-30">
          <NMForm
            ref={formRef}
            direction="vertical"
            className="flex"
            fields={computedFields}
            getOptions={getOptions}
            formData={formValues}
            onChange={handleFormChange}
          />
        </Col>

        <Col
          className="w-30 pl-30 mt-30"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            gap: 10,
            flexWrap: "nowrap",
          }}
        >
          <div style={{ display: "flex", gap: 10, flexWrap: "nowrap" }}>
            <Button label="Clear" icon="CANCEL" onClick={handleClear} />
            <Button
              icon="SEARCH"
              label="Search"
              disabled={!canSearch}
              onClick={handleSearch}
            />
            <Button
              icon="CHECK_CONFIRM"
              label="Execute"
              disabled={!canExecute}
              onClick={handleExecute}
            />
          </div>
        </Col>
      </Row>

      <div className="w-83 pl-30 mt-20 h-80">
        <DataGridView
          columns={Columns}
          rowKey="id"
          dataSource={pagedRows}
          pagination={{
            totalCount: tableRows.length,
            pageSize: PAGE_SIZE,
            defaultPage: 1,
            recentFlag: false,
            onPageChange: (nextPage) => setPage(Number(nextPage) || 1),
          }}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys),
          }}
          EmptyComponent={<div>There is no data!</div>}
        />
      </div>
    </>
  );
};
